/**
 * Enhanced Contractors List Component
 * Modern UI with improved organization and purple theme
 */

import React, { useEffect, useState } from 'react';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';

import { getAllContractors } from '../api/contractors_management';
import AddContractorForm from './AddContractorForm';
import { Button } from './ui/Button';
import { StatusBadge } from './ui/StatusBadge';
import { LoadingSkeleton } from './ui/LoadingSkeleton';
import { colors } from '../theme/colors';
import { shadows } from '../theme/shadows';

interface ContractorData {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  is_active: boolean;
  registration_time: string;
}

const StyledDataGrid = styled(DataGrid)({
  border: `1px solid ${colors.neutral[200]}`,
  borderRadius: 16,
  backgroundColor: colors.background.paper,
  boxShadow: 'none',
  fontFamily: 'inherit',
  '& .MuiDataGrid-columnHeaders': {
    backgroundColor: colors.neutral[50],
    color: colors.text.primary,
    borderBottom: `2px solid ${colors.neutral[200]}`,
    borderRadius: 0,
    fontSize: '0.8125rem',
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    minHeight: '56px !important',
    maxHeight: '56px !important',
  },
  '& .MuiDataGrid-columnHeaderTitle': {
    fontWeight: 600,
    color: colors.text.secondary,
  },
  '& .MuiDataGrid-columnSeparator': {
    display: 'none',
  },
  '& .MuiDataGrid-row': {
    transition: 'all 0.15s ease-in-out',
    cursor: 'pointer',
    minHeight: '72px !important',
    '&:hover': {
      backgroundColor: colors.neutral[50],
      boxShadow: 'inset 3px 0 0 0 ' + colors.primary.main,
    },
  },
  '& .MuiDataGrid-cell': {
    borderBottom: `1px solid ${colors.neutral[100]}`,
    display: 'flex',
    alignItems: 'center',
    padding: '16px',
    fontSize: '0.9375rem',
    '&:focus': {
      outline: 'none',
    },
    '&:focus-within': {
      outline: 'none',
    },
  },
  '& .MuiDataGrid-footerContainer': {
    borderTop: `2px solid ${colors.neutral[200]}`,
    backgroundColor: colors.neutral[50],
    minHeight: '60px',
  },
  '& .MuiTablePagination-root': {
    color: colors.text.secondary,
  },
});

const HeaderBox = styled(Box)({
  padding: '32px 0',
  marginBottom: 32,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

const ContractorsListEnhanced: React.FC = () => {
  const [data, setData] = useState<ContractorData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await getAllContractors();
      setData(response || []);
    } catch (error) {
      console.error('Error fetching contractors:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDelete = async (id: number) => {
    const confirmed = window.confirm(`Вы уверены, что хотите удалить контрактора?`);
    if (confirmed) {
      // TODO: Implement delete API
      console.log('Delete contractor', id);
    }
  };

  const renderStatusBadge = (params: GridRenderCellParams) => {
    const status = params.row.is_active as boolean;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
        <StatusBadge status={status ? 'active' : 'inactive'} label={status ? 'Активен' : 'Неактивен'} />
      </Box>
    );
  };

  const renderDeleteButton = (params: GridRenderCellParams) => (
    <Tooltip title="Удалить контрактора">
      <IconButton
        onClick={() => handleDelete(params.row.id as number)}
        disabled={isLoading}
        size="small"
        sx={{
          color: colors.error.main,
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            color: colors.error.dark,
            transform: 'scale(1.1)',
            backgroundColor: 'rgba(255, 107, 107, 0.1)',
          },
        }}
      >
        <DeleteIcon />
      </IconButton>
    </Tooltip>
  );

  const columns: GridColDef[] = [
    {
      field: 'username',
      headerName: 'Контрактор',
      flex: 1,
      minWidth: 260,
      renderCell: (params) => (
        <Box>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              color: colors.text.primary,
              fontWeight: 500,
              mb: 0.25,
            }}
          >
            {params.row.first_name} {params.row.last_name}
          </Typography>
          <Typography
            sx={{
              fontFamily: '"JetBrains Mono", monospace',
              fontSize: '0.8125rem',
              color: colors.text.secondary,
            }}
          >
            @{params.value}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'email',
      headerName: 'Email',
      width: 240,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: colors.text.secondary,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: 'is_active',
      headerName: 'Статус',
      width: 130,
      renderCell: renderStatusBadge,
    },
    {
      field: 'registration_time',
      headerName: 'Дата регистрации',
      width: 180,
      renderCell: (params) => (
        <Typography
          sx={{
            fontSize: '0.875rem',
            color: colors.text.secondary,
          }}
        >
          {params.value
            ? new Date(params.value).toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })
            : '—'}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      align: 'right',
      headerAlign: 'right',
      renderCell: renderDeleteButton,
    },
  ];

  if (isLoading && data.length === 0) {
    return (
      <Box sx={{ m: 4 }}>
        <LoadingSkeleton type="table" rows={8} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1400px', mx: 'auto', px: 4, py: 5 }}>
      <HeaderBox>
        <Box>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 600,
              color: colors.text.primary,
              mb: 1,
              fontSize: '2rem',
            }}
          >
            Контракторы
          </Typography>
          <Typography
            sx={{
              fontSize: '0.9375rem',
              color: colors.text.secondary,
              fontWeight: 400,
            }}
          >
            {data.length}
          </Typography>
        </Box>
        <Button variant="gradient" startIcon={<PersonAddIcon />} onClick={() => setIsAddDialogOpen(true)}>
          Зарегистрировать контрактора
        </Button>
      </HeaderBox>

      <Box sx={{ width: '100%' }}>
        <StyledDataGrid
          rows={data}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{ field: 'registration_time', sort: 'desc' }],
            },
            pagination: {
              paginationModel: { pageSize: 25 },
            },
          }}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          loading={isLoading}
          autoHeight
          getRowHeight={() => 'auto'}
          sx={{
            '& .MuiDataGrid-row': {
              minHeight: '72px !important',
            },
          }}
        />
      </Box>

      <Dialog open={isAddDialogOpen} onClose={() => setIsAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <AddContractorForm onSuccess={fetchData} />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContractorsListEnhanced;
